import * as React from 'react';
import { Overlay } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import { horizontallyCenterDecorator } from 'webapp/decorators/center';
import ImageBase from 'webapp/components/image';
import { Theme } from 'webapp/theme';
import { ZIndex } from 'webapp/z-index';
import { useProductImagesGalleryContext } from './ProductImages';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { scrollbarDecorator } from './scrollbar';
import { useMatchSmallViewport } from '@avensia/excite';
import { CloseIcon } from 'webapp/components/icons';
import { MobileFullscreenGallery } from './MobileFullscreenGallery';
import { applyImageBackground } from './helpers';
import { Video } from 'webapp/components/video/Video';
import { VideoPlaceholder } from 'webapp/components/video/VideoPlaceholder';
import { isVideo } from 'webapp/components/video/checkForVideo';
import { Button } from 'webapp/components/button';

type Props = {
  images: Excite.ContentApi.ImageApiModel[];
};

export default React.memo(function ProductImageFullscreenGallery({ images = [] }: Props) {
  const isSmallViewport = useMatchSmallViewport();
  const { isOpen, setIsOpen, activeSlide } = useProductImagesGalleryContext();
  const [currentIndex, setSlide] = React.useState(activeSlide);
  const [zoomedIn, setZoomedIn] = React.useState(false);

  React.useEffect(() => {
    setSlide(activeSlide);
  }, [activeSlide]);

  return (
    <StyledOverlay zIndex={ZIndex.Overlay} isEnabled={isOpen} onClose={() => setIsOpen(false)}>
      {isSmallViewport ? (
        <MobileFullscreenGallery images={images} activeSlide={activeSlide} />
      ) : (
        <>
          <OverlayInner>
            {images.map((image, index) =>
              isVideo(image.url) ? (
                <Video
                  url={image.url}
                  css={styled(currentIndex === index ? currentImageDecorator : displayNoneDecorator)}
                />
              ) : (
                <Image
                  image={image}
                  key={index}
                  css={styled(currentIndex === index && currentImageDecorator, zoomedIn && zoomedInDecorator)}
                  preset={'large'}
                  onClick={() => !isSmallViewport && setZoomedIn(!zoomedIn)}
                />
              ),
            )}
          </OverlayInner>
          {images.length > 1 && (
            <Thumbnails>
              {images.map((image, index) => (
                <ThumbnailWrapper key={index} css={currentIndex === index && currentThumbnailDecorator}>
                  {isVideo(image.url) ? (
                    <VideoPlaceholder
                      onMouseEnter={() => setSlide(index)}
                      css={styled(currentIndex === index ? currentThumbnailDecorator : {}, videoIconDecorator)}
                    />
                  ) : (
                    <Thumbnail
                      image={image}
                      onMouseEnter={() => setSlide(index)}
                      css={currentIndex === index && currentThumbnailDecorator}
                    />
                  )}
                </ThumbnailWrapper>
              ))}
            </Thumbnails>
          )}
        </>
      )}
      <CloseButton disableRippleEffect onClick={() => setIsOpen(false)} as={Theme.Transparent} square>
        <CloseIcon />
      </CloseButton>
    </StyledOverlay>
  );
});

const StyledOverlay = styled(Overlay, {
  backgroundColor: 'white',
});

const displayNoneDecorator = styled({ display: 'none' });

const OverlayInner = styled.div(scrollbarDecorator, {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  height: '100%',
  width: '100%',
  pointerEvents: 'none',
  overflowY: 'auto',
});

const Image = styled(
  ImageBase,
  horizontallyCenterDecorator,
  createMaxSmallViewportDecorator({
    cursor: 'default',
  }),
  {
    width: 'auto',
    height: '100%',
    display: 'none',
    margin: { x: 'auto' },
    objectFit: 'contain',
    pointerEvents: 'auto',
    cursor: 'zoom-in',
    top: 0,
  },
);

const videoIconDecorator = styled({
  width: 80,
  cursor: 'pointer',
});

const currentImageDecorator = styled({
  pointerEvents: 'auto',
  display: 'block',
});

const zoomedInDecorator = styled({
  width: '100%',
  height: 'auto',
  cursor: 'zoom-out',
});

const Thumbnails = styled.div(scrollbarDecorator, {
  position: 'absolute',
  left: 0,
  top: t => t.sharedSpacing.m,
  display: 'flex',
  flexDirection: 'column',
  gap: t => t.sharedSpacing.xs,
  overflow: 'auto',
  padding: { x: t => t.sharedSpacing.m },
  height: t => `calc(80vh - ${2 * t.sharedSpacing.m}px)`,
});

const ThumbnailWrapper = styled.div(
  {
    position: 'relative',
    border: { xy: { width: 2, color: t => t.thumbnailBorder, style: 'solid' } },
    flexShrink: 0,
    flexGrow: 0,
    cursor: 'pointer',
  },
  applyImageBackground && {
    backgroundColor: t => t.imageBackgroundColor,
  },
);

const Thumbnail = styled(
  ImageBase,
  {
    width: 80,
    height: 'auto',
    cursor: 'pointer',
  },
  applyImageBackground && {
    mixBlendMode: 'multiply',
  },
);

const currentThumbnailDecorator = styled({
  border: { xy: { color: t => t.borderHighlight } },
});

const IconButton = styled(
  Button,
  createButtonHoverDecorator({
    backgroundColor: '#edece8',
  }),
  {
    position: 'absolute',
    fontSize: t => t.sharedFontSize.xl,
    color: 'white',
    pointerEvents: 'auto',
    borderRadius: '50%',
  },
);

const CloseButton = styled(
  IconButton,
  createMinLargeViewportDecorator({
    top: 10,
    right: 10,
  }),

  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.s,
  }),
  {
    top: 0,
    right: 0,
    color: t => t.dark.color,
  },
);
